import React, { useEffect, useState } from 'react';
import './Fournisseur.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ RÉGLAGES LARGEURS COLONNES — ajuste les valeurs px selon tes besoins
const COLUMN_WIDTHS = {
  'Fournisseur':             '200px',
  'Numero de commande':      '140px',
  'Numero de ligne':          '90px',
  'Item':                    '160px',
  'Description':             '250px',
  'Date de commande':        '120px',
  'Date de livraison':       '130px',
  'Quantite commandee':      '120px',
  'Quantité commandée':      '120px',
  'Quantite livree':         '110px',
  'Quantité livrée':         '110px',
  'RAL':                      '70px',
  'Statut AR':                '90px',
  'Approvisionneur':         '160px',
  'Horizon':                  '90px',
  'Montant':                 '110px',
  'Age':                      '70px',
};
const DEFAULT_WIDTH = '120px';

const calcTotalWidth = (cols) =>
  cols.reduce((s, c) => s + parseInt(COLUMN_WIDTHS[c] || DEFAULT_WIDTH, 10), 0);

const HIDDEN_COLUMNS = ['ID', 'Client_id'];

// ★ Présentation des colonnes
const COLONNES_NUM  = ['Numero de ligne', 'Quantite commandee', 'Quantité commandée', 'Quantite livree', 'Quantité livrée', 'Montant', 'Age', 'Prix unitaire'];
const COLONNES_MONO = ['Numero de commande', 'Item', 'Date de commande', 'Date de livraison', 'RAL'];

// ★ Dates affichées au format jj/mm/aaaa, sans heure
//   - "2026-05-09" ou "2026-05-09T00:00:00" → découpage direct (aucun décalage de fuseau)
//   - "2026-05-08T22:00:00.000Z" (date PostgreSQL envoyée en UTC) → convertie en heure locale = 09/05/2026
//   - déjà "09/05/2026" → inchangée
const pad2 = (n) => String(n).padStart(2, '0');
const formatDateFR = (val) => {
  if (val === null || val === undefined || val === '') return val;
  const s = String(val).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return val;
  const avecFuseau = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
  if (avecFuseau && !(m[4] === '00' && m[5] === '00')) {
    const d = new Date(s);
    if (!isNaN(d)) return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  return `${m[3]}/${m[2]}/${m[1]}`;
};
const estColonneDate = (col) => /date/i.test(col);

const normaliser = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Statut AR : même règle que l'original (OUI = vert, NON = rouge)
const toneStatutAR = (val) => {
  const v = String(val ?? '').toUpperCase();
  if (v === 'OUI') return 'ok';
  if (v === 'NON') return 'danger';
  return null;
};

// Horizon : couleur selon le texte
const toneHorizon = (val) => {
  const v = normaliser(val);
  if (!v) return null;
  if (/(retard|depasse|urgent|ko)/.test(v)) return 'danger';
  if (/(semaine|j\+|proche|attente|en cours)/.test(v)) return 'warn';
  if (/(ok|livre|termine|clos|solde)/.test(v)) return 'ok';
  return 'neutral';
};

const IconeLoupe = () => (
  <svg className="fr-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
  </svg>
);

const FILTRES = [
  { name: 'fournisseur',     label: 'Fournisseur',     placeholder: 'Fournisseur…' },
  { name: 'item',            label: 'Item',            placeholder: 'Item…' },
  { name: 'approvisionneur', label: 'Approvisionneur', placeholder: 'Approvisionneur…' },
  { name: 'numeroCommande',  label: 'N° commande',     placeholder: 'N° commande…' },
];

const Fournisseur = () => {
  const [donnees, setDonnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtres, setFiltres] = useState({ fournisseur: '', item: '', approvisionneur: '', numeroCommande: '' });

  useEffect(() => {
    document.title = 'MYSSA – Cmde fournisseur';
  }, []);

  useEffect(() => {
    fetch(`${API}/base_de_donnee_cmde_frn`)
      .then(r => r.json())
      .then(d => {
        // Dates converties une fois au chargement : affichage, filtres et export Excel en jj/mm/aaaa
        const lignes = (Array.isArray(d) ? d : []).map(row => {
          const r = { ...row };
          Object.keys(r).forEach(col => { if (estColonneDate(col)) r[col] = formatDateFR(r[col]); });
          return r;
        });
        setDonnees(lignes);
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const handleReset = () =>
    setFiltres({ fournisseur: '', item: '', approvisionneur: '', numeroCommande: '' });

  const columns = donnees.length > 0
    ? Object.keys(donnees[0]).filter(c => !HIDDEN_COLUMNS.includes(c))
    : [];

  const filtered = donnees.filter(l =>
    l['Fournisseur']?.toLowerCase().includes(filtres.fournisseur) &&
    l['Item']?.toLowerCase().includes(filtres.item) &&
    l['Approvisionneur']?.toLowerCase().includes(filtres.approvisionneur) &&
    l['Numero de commande']?.toLowerCase().includes(filtres.numeroCommande)
  );

  const exportToExcel = () => {
    const ws  = XLSX.utils.json_to_sheet(filtered.map(r => ({ ...r })));
    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fournisseur');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `fournisseur_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const totalWidth      = calcTotalWidth(columns);
  const nbFiltresActifs = Object.values(filtres).filter(Boolean).length;
  const nbCommandes     = new Set(filtered.map(l => l['Numero de commande'])).size;
  const nbSansAR        = filtered.filter(l => toneStatutAR(l['Statut AR']) === 'danger').length;

  const cellule = (ligne, col, j) => {
    const val = ligne[col];
    if (col === 'Statut AR' || col === 'Horizon') {
      const tone = col === 'Statut AR' ? toneStatutAR(val) : toneHorizon(val);
      return (
        <td key={j} title={val ?? ''}>
          {tone ? <span className={`fr-badge fr-badge-${tone}`}>{val}</span> : <span className="fr-muted">{val || '—'}</span>}
        </td>
      );
    }
    const classes = [
      COLONNES_NUM.includes(col) ? 'num fr-mono' : '',
      COLONNES_MONO.includes(col) ? 'fr-mono' : '',
      col === 'Numero de commande' ? 'fr-strong' : '',
      col === 'Fournisseur' ? 'fr-client' : '',
    ].filter(Boolean).join(' ');
    return <td key={j} className={classes} title={val ?? ''}>{val}</td>;
  };

  return (
    <div className="fr-page">
      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Fournisseur.css,
            y compris la classe "num" qui aligne les quantités / montants à droite) */}
      <style>{`
        .fr-table th, .fr-table th.num,
        .fr-table td, .fr-table td.num { text-align: center !important; vertical-align: middle; }
      `}</style>
      <div className="fr-container">

        {/* ── En-tête ── */}
        <div className="fr-header">
          <div className="fr-title-row">
            <span className="fr-accent" />
            <h1 className="fr-title">Commandes fournisseurs</h1>
            <span className="fr-chip-dept">APPRO</span>
          </div>
         
        </div>

        {/* ── Filtres ── */}
        <div className="fr-filters">
          {FILTRES.map(({ name, label, placeholder }) => (
            <div key={name} className="fr-search">
              <IconeLoupe />
              <input
                type="text"
                className={`fr-input ${filtres[name] ? 'active' : ''}`}
                placeholder={placeholder}
                aria-label={`Filtrer par ${label}`}
                value={filtres[name]}
                onChange={e => setFiltres({ ...filtres, [name]: e.target.value.toLowerCase() })}
              />
              {filtres[name] && (
                <button type="button" className="fr-clear" aria-label={`Effacer ${label}`} onClick={() => setFiltres({ ...filtres, [name]: '' })}>×</button>
              )}
            </div>
          ))}

          <button type="button" className="fr-btn fr-btn-ghost" onClick={handleReset} disabled={nbFiltresActifs === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            Réinitialiser{nbFiltresActifs ? ` (${nbFiltresActifs})` : ''}
          </button>

          <button type="button" className="fr-btn fr-btn-primary" onClick={exportToExcel} disabled={filtered.length === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
            Export Excel
          </button>
        </div>

        {/* ── Tableau ── */}
        <div className="fr-card fr-table-card">
          <div className="fr-table-head">
            <div className="fr-table-head-left">
              <span className="fr-table-title">Lignes de commande</span>
              <span className="fr-count">
                {filtered.length}
                {filtered.length !== donnees.length && <span className="fr-count-total"> / {donnees.length}</span>}
              </span>
            </div>
            <div className="fr-table-head-right">
              {nbSansAR > 0 && <span className="fr-badge fr-badge-danger">{nbSansAR} sans AR</span>}
              <span className="fr-meta">{nbCommandes} commande{nbCommandes > 1 ? 's' : ''}</span>
            </div>
          </div>

          <div className="fr-table-scroll">
            {loading ? (
              <div className="fr-empty">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="fr-empty">
                {donnees.length === 0 ? 'Aucune commande fournisseur.' : 'Aucune ligne ne correspond aux filtres.'}
              </div>
            ) : (
              /* tableLayout fixed + largeur totale = colonnes figées (★ COLUMN_WIDTHS en haut du fichier) */
              <table className="fr-table" style={{ width: `${totalWidth}px` }}>
                <colgroup>
                  {columns.map(c => <col key={c} width={COLUMN_WIDTHS[c] || DEFAULT_WIDTH} />)}
                </colgroup>
                <thead>
                  <tr>
                    {columns.map(c => (
                      <th key={c} className={COLONNES_NUM.includes(c) ? 'num' : ''} title={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ligne, i) => (
                    <tr key={i} className={toneStatutAR(ligne['Statut AR']) === 'danger' ? 'fr-row-danger' : ''}>
                      {columns.map((c, j) => cellule(ligne, c, j))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fournisseur;